// ============================================
// Flashcard Component with 3D Flip Animation
// Design System v2.0 - Polished visuals
// ============================================

import { m, AnimatePresence } from 'framer-motion';
import { cardFlip, cardSwipe, audioButton } from '@/animations';
import type { Word, SRSState } from '@/types';
import { SRSBadge } from './ui/SRSBadge';

// ============================================
// Helper to map SRS state to CSS class
// ============================================

function getSRSClass(state: SRSState): string {
  if (state === 'new') return 'flashcard-srs-new';
  if (state.includes('learning')) return 'flashcard-srs-learning';
  if (state.includes('review')) return 'flashcard-srs-review';
  return 'flashcard-srs-mastered';
}

// ============================================
// SRS State Indicator — the animated flashcard-corner badge
// ============================================

interface SRSIndicatorProps {
  state: SRSState;
}

function SRSIndicator({ state }: SRSIndicatorProps) {
  return (
    <SRSBadge state={state} animated className="absolute top-4 left-4 w-9 h-9 shadow-2" />
  );
}

// ============================================
// Audio Button
// ============================================

interface AudioButtonProps {
  isPlaying: boolean;
  isLoading: boolean;
  onClick: () => void;
}

function AudioButton({ isPlaying, isLoading, onClick }: AudioButtonProps) {
  const getAnimationState = () => {
    if (isLoading) return 'loading';
    if (isPlaying) return 'playing';
    return 'idle';
  };

  return (
    <m.button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="audio-button absolute top-4 right-4 w-11 h-11
                 flex items-center justify-center text-xl
                 focus:outline-none focus-visible:shadow-glow-primary"
      variants={audioButton}
      animate={getAnimationState()}
      aria-label="Play pronunciation audio"
      disabled={isLoading}
    >
      🔊
    </m.button>
  );
}

// ============================================
// Flashcard Component
// ============================================

interface FlashcardProps {
  word: Word;
  isFlipped: boolean;
  onFlip: () => void;
  srsState: SRSState;
  direction?: number; // 1 for next, -1 for previous (for swipe animation)
  onPlayAudio: () => void;
  isAudioPlaying: boolean;
  isAudioLoading: boolean;
}

export function Flashcard({
  word,
  isFlipped,
  onFlip,
  srsState,
  direction = 1,
  onPlayAudio,
  isAudioPlaying,
  isAudioLoading
}: FlashcardProps) {
  const srsClass = getSRSClass(srsState);

  return (
    <AnimatePresence mode="wait" custom={direction}>
      <m.div
        key={word.japanese}
        className="relative w-full max-w-md mx-auto"
        style={{ perspective: '1000px' }}
        variants={cardSwipe}
        initial="enter"
        animate="center"
        exit="exit"
        custom={direction}
      >
        <m.div
          className="relative w-full min-h-[320px] cursor-pointer"
          style={{ transformStyle: 'preserve-3d' }}
          onClick={onFlip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onFlip();
            }
          }}
          variants={cardFlip}
          animate={isFlipped ? 'back' : 'front'}
          tabIndex={0}
          role="button"
          aria-label={`Flashcard: ${word.japanese}. Press Enter or Space to flip.`}
        >
          {/* Front of card */}
          <div
            className={`
              flashcard ${srsClass}
              absolute inset-0
              flex flex-col items-center justify-center p-8 pt-10
              ${isFlipped ? 'invisible' : ''}
            `}
            style={{ backfaceVisibility: 'hidden' }}
          >
            <SRSIndicator state={srsState} />
            <AudioButton
              isPlaying={isAudioPlaying}
              isLoading={isAudioLoading}
              onClick={onPlayAudio}
            />

            <div className="text-center mt-2">
              <div className="text-sm mb-3 uppercase tracking-widest font-medium" style={{ color: 'var(--color-text-subtle)' }}>
                Tap to flip
              </div>
              <div className="text-5xl font-bold font-japanese mb-4 leading-tight" style={{ color: 'var(--color-text)' }}>
                {word.japanese}
              </div>
              {word.japanese !== word.reading && (
                <div className="text-xl font-japanese" style={{ color: 'var(--color-text-muted)' }}>
                  {word.reading}
                </div>
              )}
            </div>
          </div>

          {/* Back of card */}
          <div
            className={`
              flashcard ${srsClass}
              absolute inset-0
              flex flex-col items-center justify-center p-8 pt-10
              ${!isFlipped ? 'invisible' : ''}
            `}
            style={{
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
            }}
          >
            <SRSIndicator state={srsState} />
            <AudioButton
              isPlaying={isAudioPlaying}
              isLoading={isAudioLoading}
              onClick={onPlayAudio}
            />

            <div className="text-center mt-2">
              <div className="text-4xl font-bold font-japanese mb-2 leading-tight" style={{ color: 'var(--color-text)' }}>
                {word.japanese}
              </div>
              {word.japanese !== word.reading && (
                <div className="text-lg font-japanese mb-5" style={{ color: 'var(--color-text-muted)' }}>
                  {word.reading}
                </div>
              )}
              <div className="w-16 h-px mx-auto mb-5" style={{ background: `linear-gradient(to right, transparent, var(--color-border), transparent)` }} />
              <div className="text-2xl font-semibold text-gradient-primary">
                {word.meaning}
              </div>
              <div className="mt-4 text-sm capitalize tracking-wide" style={{ color: 'var(--color-text-subtle)' }}>
                {word.type.replace('-', ' ')}
              </div>
            </div>
          </div>
        </m.div>
      </m.div>
    </AnimatePresence>
  );
}
